import { BreadCrumb } from 'primereact/breadcrumb';
import { MenuItem } from 'primereact/menuitem';
import { Dispatch, FC, SetStateAction, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyLoadQuery } from 'react-relay';
import { getSkillAssessmentsByStudentId } from '../../graphQLOperations/IndividualStudentReport';
import { IndividualStudentReportAssessmentsQuery } from '../../graphQLOperations/__generated__/IndividualStudentReportAssessmentsQuery.graphql';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { format } from 'date-fns';
import { modalInternalInfo, reviewScoreState } from './IndividualStudentReportSkillsModal';
import { IndividualReportAssessmentPlayer } from './IndividualReportAssessmentPlayer';
import { AssessmentsReportWarning } from './Banners/AssessmentsReportWarning';
import { SelectedSkillIndividualReport, StudentInfoInterface } from './IndividualReportWrapper';
import { formatAssessmentTime } from '../../businessLogicService/assessmentActiveTime';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  id: string;
  setIndividualStudentId: Dispatch<SetStateAction<string | null>>;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkillIndividualReport | null>>;
  studentInfo?: StudentInfoInterface | null;
  selectedSkill: SelectedSkillIndividualReport | null;
}

export interface CurrentAssessment {
  sessionId: string;
  activityId: string;
  userId: string;
  name: string;
}
export const IndividualReportAssessments: FC<Props> = ({
  setIndividualStudentId,
  studentInfo,
  selectedSkill,
  id,
  setSelectedSkill,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [currentAssessmentInfo, setCurrentAssessmentInfo] = useState<CurrentAssessment | null>(null);

  const { skillAssessmentsByStudentId } = useLazyLoadQuery<IndividualStudentReportAssessmentsQuery>(
    getSkillAssessmentsByStudentId,
    {
      studentId: id || '',
      filter: {
        skillId: selectedSkill?.skillId || '',
      },
    },
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (selectedSkill?.lastSession) {
      setActiveIndex(
        skillAssessmentsByStudentId?.nodes?.findIndex(ska => ska.sessionId === selectedSkill.lastSession) || 0,
      );
    } else {
      setActiveIndex(0);
    }
  }, [selectedSkill?.skillId]);

  useEffect(() => {
    if (activeIndex !== null && skillAssessmentsByStudentId?.nodes?.length) {
      const assessment = skillAssessmentsByStudentId.nodes[activeIndex];
      setCurrentAssessmentInfo({
        sessionId: assessment.sessionId,
        activityId: assessment.activityId || '',
        userId: assessment.publicUserId || '',
        name: assessment.skill?.name || '',
      });
    }
  }, [activeIndex]);

  const navigate = useNavigate();
  const home = { icon: 'nc_icon-nc_home icon-sm', command: () => navigate('/') };
  const breadcrumbItems: MenuItem[] = [
    { label: 'Monitor', command: () => navigate('/quick-data') },
    {
      label: 'Progress',
      command: () => setIndividualStudentId(null),
    },
    {
      label: 'Individual report',
      command: () => setSelectedSkill(null),
      template: (_item, options) => (
        <a href="#" className={`${options.className} ph-no-autocapture`} onClick={options.onClick}>
          <span className={options.labelClassName}>
            <MaskedStudentData student={{ publicUserId: studentInfo?.publicUserId }} type={PiiTypes.FullName}>
              {`${studentInfo?.firstName} ${studentInfo?.lastName}`}
            </MaskedStudentData>{' '}
            Individual report
          </span>
        </a>
      ),
    },
    { label: 'Assessment results' },
  ];
  return (
    <div className="tw-flex-col tw-gap-y-3 tw-my-3 tw-w-[80%]">
      <BreadCrumb model={breadcrumbItems} home={home} />
      <div className="tw-p-8 tw-border tw-border-solid tw-rounded-2xl tw-border-[#D3D3D3] tw-bg-white">
        <AssessmentsReportWarning skillAssessments={skillAssessmentsByStudentId} />
        <Accordion activeIndex={activeIndex} onTabChange={e => setActiveIndex(e.index as number)}>
          {skillAssessmentsByStudentId?.nodes?.length &&
            skillAssessmentsByStudentId.nodes.length > 0 &&
            skillAssessmentsByStudentId?.nodes?.map((skAssessment, inx) => {
              const assessmentTime = formatAssessmentTime(skAssessment);

              return (
                <AccordionTab
                  key={skAssessment.id}
                  className="tw-my-3"
                  header={
                    <div className="tw-text-neutral-600 tw-flex tw-items-center">
                      <span className="tw-text-neutral-900 tw-font-semibold">
                        {format(skAssessment.sessionCompletedOn, 'PP')}
                      </span>
                      <span className="tw-mx-4 tw-text-neutral-900">|</span>
                      <span className="tw-text-neutral-900">
                        Time:{' '}
                        <b>
                          <span aria-hidden="true">{assessmentTime.display}</span>
                          <span className="tw-sr-only">{assessmentTime.label}</span>
                        </b>
                      </span>
                      <span className="tw-mx-4 tw-text-neutral-900">|</span>
                      <span>
                        <span
                          className={`${
                            modalInternalInfo[reviewScoreState(skAssessment.score)].classNames
                          } tw-w-[5rem] tw-h-[90%] tw-font-semibold tw-py-1 tw-px-2 tw-rounded-md tw-flex tw-items-center

                    `}
                        >
                          {modalInternalInfo[reviewScoreState(skAssessment.score)].icon}
                          <span className="tw-text-neutral-900 ml-2">
                            {skAssessment.score === 0
                              ? skAssessment.score
                              : `${skAssessment.score > 0 ? `${skAssessment.score}0` : skAssessment.score}`}
                            %
                          </span>
                        </span>
                      </span>
                    </div>
                  }
                >
                  {activeIndex !== null && currentAssessmentInfo && activeIndex === inx && (
                    <Suspense>
                      <IndividualReportAssessmentPlayer currentAssessmentInfo={currentAssessmentInfo} />
                    </Suspense>
                  )}
                </AccordionTab>
              );
            })}
        </Accordion>
        {!skillAssessmentsByStudentId?.nodes?.length && (
          <div className="tw-w-[100%]">
            <p className="tw-w-[100%] tw-bg-neutral-100 tw-rounded-xl tw-py-6 tw-text-center">
              No assessments submitted
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
